const projects = [
  {
    id: 'mnist',
    name: 'MNIST digit recognition',
    category: 'Applied AI · Self-hosted',
    headline: 'A handwritten digit.',
    accent: 'A neural network.',
    description: 'Draw a number and watch a trained model interpret it. You can inspect exactly what the model sees, then compare its predictions. The app runs on my own Linux homelab.',
    tech: ['Python', 'TensorFlow', 'FastAPI', 'Docker'],
    github: 'https://github.com/SakariaAhmed/Neural-Network-MNIST',
    live: 'https://linux-lab.tail15c91a.ts.net/',
    action: 'Draw a digit',
    availability: 'Personal homelab demo — it may occasionally be offline.',
    previewLabel: 'The real application',
    previewMeta: '28 × 28 → 0–9',
    images: [{ src: '/projects/mnist-homelab.jpg', alt: 'MNIST drawing canvas, pixel preview and model prediction scores', width: 1280, height: 979 }],
    caption: 'From a browser sketch to model inference.',
    related: { href: '#homelab', label: 'How it runs' },
    steps: [
      ['You draw.', 'A digit, from zero to nine.'],
      ['It sees pixels.', 'Your drawing becomes a 28 × 28 image.'],
      ['You see its guess.', 'Compare the model’s scores for all ten digits.'],
    ],
    detailLabel: 'Beyond the model',
    detail: 'Validated requests, rate limits, model checksums and private monitoring. A learning project that also explores how to operate an AI service.',
  },
  {
    id: 'pricepulse',
    name: 'PricePulse',
    category: 'Android · Team project',
    headline: 'Every kilowatt-hour.',
    accent: 'A clearer cost.',
    description: 'An Android app for comparing hourly electricity prices and estimating what household appliances cost to run. Built by a team of six as part of IN2000 at the University of Oslo.',
    tech: ['Kotlin', 'Jetpack Compose', 'API integration'],
    github: 'https://github.com/SakariaAhmed/PricePulse',
    previewLabel: 'Application screenshots',
    previewMeta: 'Android',
    imageLayout: 'phones',
    images: [
      { src: '/projects/pricepulse-home.png', alt: 'PricePulse home screen with electricity prices', width: 1080, height: 2400 },
      { src: '/projects/pricepulse-appliances.png', alt: 'PricePulse appliance cost calculator', width: 1080, height: 2400 },
    ],
    caption: 'Hourly prices and appliance costs, side by side.',
    steps: [
      ['Compare the hours.', 'See the current price and how it changes throughout the day.'],
      ['Choose an appliance.', 'Explore the cost of a washing machine, oven, heater or shower.'],
      ['Estimate the cost.', 'Compare usage costs for different times and durations.'],
    ],
    detailLabel: 'My contribution',
    detail: 'Settings, dialogs and tests in Kotlin and Jetpack Compose. A collaborative university project with layouts for phones and tablets, in Norwegian and English.',
  },
  {
    id: 'pokemon-search',
    name: 'Pokémon Search',
    category: 'Web app · API integration',
    headline: 'A name to look up.',
    accent: 'A Pokémon to discover.',
    description: 'A small search app that turns a Pokémon name into a picture, types and base stats. Built with JavaScript and PokeAPI, with loading feedback and clear messages when a search fails.',
    tech: ['JavaScript', 'CSS', 'PokeAPI'],
    github: 'https://github.com/SakariaAhmed/Pokemon-search',
    live: 'https://pokemon-search-info.netlify.app/',
    action: 'Find a Pokémon',
    previewLabel: 'Application preview',
    previewMeta: 'PokeAPI',
    images: [{ src: '/projects/pokemon-test.png', alt: 'Pokémon Search interface showing a search result and stats', width: 1320, height: 1336 }],
    caption: 'A simple search, with the details in one place.',
    steps: [
      ['Enter a name.', 'Type a Pokémon name and search with the button or Enter.'],
      ['Fetch its data.', 'The app requests the matching record from PokeAPI.'],
      ['Explore the result.', 'See its sprite, types, HP, attack and defense.'],
    ],
    detailLabel: 'The focus',
    detail: 'Asynchronous requests, DOM updates and useful loading and error states. A focused exercise in connecting an interface to an external API.',
  },
  {
    id: 'edentri',
    name: 'Edentri Redesign',
    category: 'Frontend · Design concept',
    headline: 'A complex service.',
    accent: 'A clearer first impression.',
    description: 'A personal redesign concept for a document-intelligence website. Service pages, clear calls to action and a responsive interface explore how to make a technical product easier to understand.',
    tech: ['TypeScript', 'Next.js', 'React', 'Tailwind CSS'],
    github: 'https://github.com/SakariaAhmed/edentri-redesign',
    live: 'https://edentri-redesign.netlify.app/',
    action: 'Explore the design',
    previewLabel: 'Website concept',
    previewMeta: 'Responsive web',
    images: [{ src: '/projects/edentri-test.png', alt: 'Edentri redesign concept with orange accents and a document-intelligence headline', width: 2486, height: 1498 }],
    caption: 'An interface concept for a technical service.',
    steps: [
      ['Clear hierarchy.', 'A direct introduction, service sections and visible next steps.'],
      ['Reusable components.', 'Shared navigation, buttons, cards and form elements.'],
      ['Responsive layouts.', 'A website designed to adapt from desktop to smaller screens.'],
    ],
    detailLabel: 'Design exploration',
    detail: 'A personal frontend exploration of information architecture, reusable components and responsive product pages for a technical service.',
  },
  {
    id: 'qr-studio',
    name: 'QR Studio',
    category: 'Browser tool · Python CLI',
    headline: 'An idea to share.',
    accent: 'A code to scan.',
    description: 'Create QR codes for websites, text and Wi-Fi in a browser workspace. Customize the preview and export a PNG or SVG. QR content is processed on your device, with no account or content upload.',
    tech: ['JavaScript', 'Python', 'QR encoding'],
    github: 'https://github.com/SakariaAhmed/QR-generator',
    live: '/tools/qr/',
    action: 'Create a QR code',
    previewLabel: 'The real application',
    previewMeta: 'PNG / SVG',
    images: [{ src: '/projects/qr-studio.jpg', alt: 'QR Studio workspace with content controls, QR preview and export options', width: 1280, height: 1030 }],
    caption: 'From a link or message to a downloadable code.',
    steps: [
      ['Choose the content.', 'Start with a website, text or Wi-Fi connection details.'],
      ['Make it yours.', 'Choose an ink color and error correction, with a live preview.'],
      ['Export and scan.', 'Download a high-resolution PNG or SVG and check it before printing.'],
    ],
    detailLabel: 'Beyond the browser',
    detail: 'Includes a Python command-line tool. Automated checks decode generated PNGs and cover Unicode, Wi-Fi credentials, export sizes and invalid inputs.',
  },
]

export default projects
